import { useLayoutEffect, useRef } from "react";

interface FitTextProps {
  children: React.ReactNode;
  // Starting font size, in px.
  max: number;
  // The text never goes below this size.
  min?: number;
  className?: string;
}

// One line of text that never wraps and is never cut off: if it is wider than
// its container, the font size steps down 1px at a time until it fits.
export const FitText = ({ children, max, min = 14, className }: FitTextProps) => {
  const ref = useRef<HTMLSpanElement>(null);

  useLayoutEffect(() => {
    const element = ref.current;
    if (!element) return;

    const fit = () => {
      let size = max;
      element.style.fontSize = `${size}px`;
      while (size > min && element.scrollWidth > element.clientWidth + 0.5) {
        size -= 1;
        element.style.fontSize = `${size}px`;
      }
    };

    fit();
    // Measure again once the title font has loaded, and when the width changes.
    document.fonts?.ready.then(fit);
    const observer = new ResizeObserver(fit);
    if (element.parentElement) observer.observe(element.parentElement);
    return () => observer.disconnect();
  }, [children, max, min]);

  return (
    <span ref={ref} className={`ds-fit ${className ?? ""}`}>
      {children}
    </span>
  );
};
